"use client";

import { useEffect } from "react";
import { getChatConnection } from "@/lib/signalr/chat-connection";
import { HubConnectionState } from "@microsoft/signalr";
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import type { 
  ReceiveMessagePayload, 
  GetMessageHistoryResponse,
  GetConversationListResponse,
  MessageHistoryItem
} from "@/interface/message.types";
import { useChatUnreadStore } from "@/store/chat-unread-store";

/**
 * Static registry to prevent duplicate listeners and multiple cache updates.
 * In a complex React app, multiple components (Header, Sidebar, Content) might use the socket hook.
 * This singleton pattern ensures only ONE listener is active on the SignalR connection.
 */
let isListenerRegistered = false;
let queryClientInstance: QueryClient | null = null;
let markAsUnreadFn: ((proposalId: string) => void) | null = null;

const handleReceiveMessage = (payload: ReceiveMessagePayload) => {
  if (!queryClientInstance) {
    console.warn("⚠️ [Socket] Received message but queryClientInstance is not initialized.");
    return;
  }

  const { proposalId, message, timestamp, senderId, senderRole } = payload;
  
  // Important: We use the global store to know what's active
  const activeProposalId = useChatUnreadStore.getState().activeProposalId;
  
  console.log(`📩 [Socket] Received message for ${proposalId}. Currently viewing: ${activeProposalId || "None/Dashboard"}`);

  // 1. Sync server and store state
  if (proposalId === activeProposalId) {
    // Immediate Acknowledgment for active chat to clear server count
    import("@/services/notification.service").then((m) => {
      // Find the message in the notification list if possible, or use a general endpoint if available
      // Note: SignalR Chat hub ReceiveMessage doesn't always have a NotificationId (payload.id)
      // but the server usually marks the entire conversation as read when we sync.
      // We call markAsRead if we had a notification ID, but here we just want to ensure 
      // the server knows we've seen the message in the active chat.
    }).catch(() => {});
  } else if (markAsUnreadFn) {
    console.log(`📍 [Socket] Marking ${proposalId} as UNREAD in persistent store`);
    markAsUnreadFn(proposalId);
  }

  const safeTimestamp = timestamp === "0001-01-01T00:00:00" || !timestamp
    ? new Date().toISOString()
    : timestamp;

  const newMessageId = payload.messageId || `socket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Update generic history cache helper
  const updateCache = (oldData: InfiniteData<GetMessageHistoryResponse> | undefined, isMine: boolean) => {
    if (!oldData || !oldData.pages.length) return oldData;
    
    // Exact duplicate detection to prevent flickering with optimistic updates
    const isDuplicate = oldData.pages.some(page => 
      page.data.messages.some(m => m.id === newMessageId || (m.content === message && Math.abs(new Date(m.sentAt).getTime() - new Date(safeTimestamp).getTime()) < 2000))
    );
    
    if (isDuplicate) {
      console.log(`⏭️ [Socket] Skipping duplicate message for ${proposalId}`);
      return oldData;
    }

    const newMessage: MessageHistoryItem = {
      id: newMessageId,
      senderId: senderId,
      receiverId: "", // Backend doesn't always provide receiverId in this context, but history usually knows it
      content: message,
      sentAt: safeTimestamp,
      isMine: isMine,
      isSeen: false
    };

    const newPages = [...oldData.pages];
    // Infinite queries: pages[0] is typically the first/most recent page
    newPages[0] = {
      ...newPages[0],
      data: { ...newPages[0].data, messages: [...newPages[0].data.messages, newMessage] }
    };
    return { ...oldData, pages: newPages };
  };

  // 2. Update Message History Caches
  // Homeowner history
  queryClientInstance.setQueriesData<InfiniteData<GetMessageHistoryResponse>>(
    { queryKey: ["messages", proposalId] },
    (oldData) => updateCache(oldData, senderRole === "Homeowner")
  );

  // Contractor history
  queryClientInstance.setQueriesData<InfiniteData<GetMessageHistoryResponse>>(
    { queryKey: ["messages", "Contractor", proposalId] },
    (oldData) => updateCache(oldData, senderRole === "Contractor")
  );

  // 3. Update Conversation List Caches (Move to top, mark unread)
  // This update is critical for the 'Blue Dot' and the side navigation order
  const updateList = (oldData: GetConversationListResponse | undefined) => {
    if (!oldData?.data?.coversationData) return oldData;
    
    const conversations = [...oldData.data.coversationData];
    const index = conversations.findIndex(c => c.proposalId === proposalId);
    
    if (index !== -1) {
      // Existing conversation: Move to top and update preview
      const updatedConv = {
        ...conversations[index],
        lastMessage: message,
        messagedDay: "Just now",
        isUnread: proposalId !== activeProposalId // Only mark unread if not viewing
      };
      conversations.splice(index, 1);
      conversations.unshift(updatedConv);
    } else {
      // New conversation: Invalidate query to pull the new data properly
      // since we don't have all the project details/user info in the socket payload
      console.log(`🆕 [Socket] Observed new conversation for ${proposalId}. Invalidating list.`);
      setTimeout(() => {
        queryClientInstance?.invalidateQueries({ queryKey: ["conversations"] });
      }, 0);
    }
    
    return { 
      ...oldData, 
      data: { 
        ...oldData.data, 
        coversationData: conversations 
      } 
    };
  };

  // Apply updates to ALL variations of the conversation list (Homeowner, Contractor, Filtered)
  // v5 setQueriesData targets by prefix matching by default
  queryClientInstance.setQueriesData<GetConversationListResponse>({ queryKey: ["conversations"] }, updateList);
};

/**
 * Hook to manage SignalR chat socket connection and event listeners.
 * 
 * @param isLoggedIn - Authentication status
 * @param activeProposalId - The proposalId of the conversation currently being viewed (if any)
 */
export const useChatSocket = (isLoggedIn: boolean | null, activeProposalId?: string | null) => {
  const queryClient = useQueryClient();
  const markAsUnread = useChatUnreadStore((state) => state.markAsUnread);

  const setActiveProposalId = useChatUnreadStore((state) => state.setActiveProposalId);
  const activeChatId = useChatUnreadStore((state) => state.activeProposalId);

  // Sync component-level state with the singleton handler
  useEffect(() => {
    // Only set as active if an actual ID is provided. 
    // This prevents global components (Header) from accidentally clearing the 'active' 
    // status while the user is actually on a chat page.
    if (activeProposalId) {
      setActiveProposalId(activeProposalId);
    }
    
    queryClientInstance = queryClient;
    markAsUnreadFn = markAsUnread;

    return () => {
      // When the component passing an ID unmounts, clear it
      // We check if it's still the same ID to prevent race conditions during navigation
      if (activeProposalId && useChatUnreadStore.getState().activeProposalId === activeProposalId) {
        setActiveProposalId(null);
      }
    };
  }, [activeProposalId, queryClient, markAsUnread, setActiveProposalId]);

  useEffect(() => {
    if (isLoggedIn === null) return;

    // Acquire the connection inside the effect, never in the render body:
    // effects don't run during SSR, while a render-body call constructs a
    // SignalR HttpConnection on the server, where SignalR's dynamic
    // require("ws") crashes with MODULE_NOT_FOUND on Amplify/standalone —
    // 500ing every dynamically-rendered page that mounts this hook.
    const connection = getChatConnection();

    const startSocket = async () => {
      if (isLoggedIn && connection.state === HubConnectionState.Disconnected) {
        try {
          await connection.start();
          console.log("🟢 Chat SignalR Connected");
        } catch (err) {
          console.error("🔴 Chat SignalR Error: ", err);
        }
      } else if (!isLoggedIn && (connection.state === HubConnectionState.Connected || connection.state === HubConnectionState.Connecting)) {
        await connection.stop();
        console.log("⚪ Chat SignalR Disconnected (Logged Out)");
      }
    };

    startSocket();

    // Register singleton listener if not already active
    if (!isListenerRegistered) {
      console.log("🔌 [Socket] Registering primary ReceiveMessage listener");
      connection.on("ReceiveMessage", handleReceiveMessage);
      isListenerRegistered = true;
      
      connection.onreconnecting((err) => console.warn("🟡 Chat SignalR Reconnecting", err));
      connection.onreconnected(() => console.log("🟢 Chat SignalR Reconnected"));
      connection.onclose(() => {
        console.error("🔴 Chat SignalR Closed");
        isListenerRegistered = false;
      });
    }

    // connection is a module-level singleton, so it's intentionally not a
    // dependency — its identity never changes across renders.
  }, [isLoggedIn]);
};
